import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { initAds, startAdsScheduler } from './ads/initAds';
import { installExternalLinkInterception } from './ads/externalLink';

// Link handling is installed immediately. The lifecycle bridge is fetched
// after app authentication; every external SDK waits for that attempt.
installExternalLinkInterception();

// Add type definition for Telegram object on window
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        openTelegramLink: (url: string) => void;
        ready: () => void;
        expand: () => void;
        close: () => void;
        isExpanded: boolean;
        enableClosingConfirmation: () => void;
        BackButton: {
          onClick: (callback: () => void) => void;
          show: () => void;
          hide: () => void;
        };
        [key: string]: any;
      };
    };
  }
}

// Функция для ожидания загрузки Telegram WebApp SDK (async скрипт)
const waitForTelegramWebApp = (maxWaitMs: number = 3000): Promise<boolean> => {
  return new Promise((resolve) => {
    // Если уже загружен - сразу возвращаем
    if (window.Telegram?.WebApp) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (window.Telegram?.WebApp) {
        clearInterval(checkInterval);
        resolve(true);
      } else if (Date.now() - startTime > maxWaitMs) {
        clearInterval(checkInterval);
        console.warn('[TelegramWebApp] SDK not loaded within timeout, continuing without it');
        resolve(false);
      }
    }, 50);
  });
};

// Инициализация Telegram WebApp с ожиданием async-загрузки
const initTelegramWebApp = async (): Promise<void> => {
  const isAvailable = await waitForTelegramWebApp(3000);
  
  if (!isAvailable) {
    console.warn('[TelegramWebApp] Running without Telegram SDK');
    return;
  }

  try {
    const webApp = window.Telegram?.WebApp;
    if (!webApp) return;

    // Let Telegram know the app is ready to be displayed
    webApp.ready();

    // Support for expanding the Mini App
    webApp.enableClosingConfirmation();

    // Optional: Enable operations with the hardware back button
    webApp.BackButton.onClick(() => {
      // Handle back button press if needed
    });
    
    console.log('[TelegramWebApp] Initialized successfully');
  } catch (e) {
    console.error('Failed to initialize Telegram WebApp:', e);
  }
};

// Инициализируем Telegram WebApp (не блокирует рендер). Runtime подключится
// после авторизации в App, а adsRuntimeReady() удержит внешние SDK до этого.
void initTelegramWebApp();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Загружаем рекламные SDK ПОСЛЕ рендера приложения (не блокируют UI)
// Используем requestIdleCallback или setTimeout для отложенной загрузки
const loadAdsWhenIdle = () => {
  // initAds ретраит каждый SDK бесконечно, поэтому один недоступный источник
  // (блокировщик, недоступный CDN) держал бы Promise.all вечно, а вместе с ним —
  // и шедулер, который к этим SDK отношения не имеет. Запускаем шедулер по таймеру,
  // не дожидаясь самых медленных сетей.
  const SCHEDULER_START_DELAY_MS = 30_000;
  let schedulerStarted = false;
  const startSchedulerOnce = () => {
    if (schedulerStarted) return;
    schedulerStarted = true;
    startAdsScheduler();
  };

  const schedulerTimer = setTimeout(startSchedulerOnce, SCHEDULER_START_DELAY_MS);

  initAds()
    .then(() => {
      console.log('[Ads] SDK initialization complete');
      clearTimeout(schedulerTimer);
      startSchedulerOnce();
    })
    .catch((err) => {
      console.error('[Ads] SDK initialization failed:', err);
      clearTimeout(schedulerTimer);
      startSchedulerOnce();
    });
};

if ('requestIdleCallback' in window) {
  (window as any).requestIdleCallback(loadAdsWhenIdle, { timeout: 2000 });
} else {
  // Fallback для Safari и старых браузеров
  setTimeout(loadAdsWhenIdle, 100);
}

// If you want to start measuring performance in your app, pass a function
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
